import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { APP_VERSION } from '../../version';

type VersionResponse = { version: string };

const RELOAD_GUARD_KEY = 'version-check-last-reload';
const RELOAD_GUARD_TTL_MS = 30_000;

// Chunk-load failures from a stale bundle look like these. Treat as
// conclusive evidence the running tab references hashes the server no
// longer has on disk — force-reload regardless of /__version state.
const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
];

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  readonly mismatch = signal(false);
  private serverVersion: string | null = null;
  private initialized = false;

  initialize(): void {
    if (this.initialized) return;
    if (!isPlatformBrowser(this.platformId)) return;
    this.initialized = true;

    this.checkVersion();

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.checkVersion());

    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe((e) => {
        // Re-poll so subsequent navigations have fresh state, but also
        // intercept now if a prior poll already flagged staleness.
        this.checkVersion();
        if (this.mismatch()) {
          this.triggerReload((e as NavigationStart).url);
        }
      });

    this.router.events
      .pipe(filter(e => e instanceof NavigationError))
      .subscribe((e) => {
        const err = e as NavigationError;
        const msg = String((err.error as { message?: string })?.message || err.error || '');
        this.forceReloadIfStale(msg, err.url);
      });

    // Safety net for chunk-load failures that DON'T flow through the Router:
    // @defer blocks and any manual dynamic import() reject outside navigation,
    // so they never emit a NavigationError. Funnel their rejections/errors
    // through the same patterns, staleness flag and reload guard.
    window.addEventListener('unhandledrejection', (event) => {
      const reason = (event as PromiseRejectionEvent).reason;
      const msg = String((reason as { message?: string })?.message || reason || '');
      this.forceReloadIfStale(msg);
    });

    window.addEventListener('error', (event) => {
      const msg = String(event.message || (event.error as { message?: string })?.message || '');
      this.forceReloadIfStale(msg);
    });
  }

  private checkVersion(): void {
    this.http.get<VersionResponse>('/__version').subscribe({
      next: (res) => {
        if (!res?.version) return;
        this.serverVersion = res.version;
        this.mismatch.set(res.version !== APP_VERSION);
      },
      error: () => { /* network blips are not signal of staleness */ },
    });
  }

  /**
   * Public escape hatch for chunk-load failures detected outside the Router
   * (the global error/unhandledrejection handlers route here). If the message
   * matches a stale-bundle pattern, flag staleness and force a cache-busted
   * reload — subject to the same 30s guard as every other reload path.
   */
  forceReloadIfStale(message: string, url?: string): void {
    if (!this.isChunkError(message)) return;
    this.mismatch.set(true);
    this.triggerReload(url || window.location.pathname + window.location.search);
  }

  private isChunkError(message: string): boolean {
    return CHUNK_ERROR_PATTERNS.some(p => message.includes(p));
  }

  private triggerReload(nextUrl: string): void {
    if (this.recentlyReloaded()) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, Date.now().toString());

    const target = nextUrl || window.location.pathname;
    const separator = target.includes('?') ? '&' : '?';
    const buster = encodeURIComponent(this.serverVersion ?? Date.now().toString());
    window.location.href = `${target}${separator}v=${buster}`;
  }

  private recentlyReloaded(): boolean {
    const last = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!last) return false;
    const elapsed = Date.now() - Number(last);
    return Number.isFinite(elapsed) && elapsed < RELOAD_GUARD_TTL_MS;
  }
}
